// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState } from 'react';
import { Modal, Button, StatusBanner, Checkbox } from '@loa/ui-components';
import { invitationApi } from '../api/invitationApi';
import type { BulkInvitationResponse } from '../types/invitation';

type SendInvitationModalProps = Readonly<{
  profileId: string;
  profileName: string;
  totalClients: number;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}>;

export function SendInvitationModal({
  profileId,
  profileName,
  totalClients,
  open,
  onClose,
  onSuccess
}: SendInvitationModalProps) {
  const [skipActive, setSkipActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<BulkInvitationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSend = async () => {
    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const response = await invitationApi.sendBulkInvitations(profileId, skipActive);
      setResult(response);

      if (response.success) {
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 2000);
      }
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Error sending invitations:', error);
      setError(error.response?.data?.message || 'Failed to send invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setError(null);
    setSkipActive(true);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} size="medium">
      <Modal.Header icon="mail" title="Send Client Invitations" onClose={handleClose} />
      <Modal.Content>
        <p>
          Send email invitations to all clients in <strong>{profileName}</strong> to register on
          the client portal.
        </p>
        <p>
          <strong>Total clients:</strong> {totalClients}
        </p>

        <div style={{ marginTop: '1em' }}>
          <Checkbox
            label="Skip clients who are already registered"
            checked={skipActive}
            onChange={(e) => setSkipActive(e.currentTarget.checked)}
          />
        </div>

        {result && (
          <div style={{ marginTop: '1.5em' }}>
            <StatusBanner
              variant={result.success ? 'success' : 'error'}
              message={
                <>
                  <strong>{result.success ? 'Invitations Sent!' : 'Partial Success'}</strong>
                  <p style={{ margin: '0.5em 0' }}>{result.message}</p>
                  <ul style={{ margin: '0.5em 0', paddingLeft: '1.5em' }}>
                    <li>Sent: {result.sent}</li>
                    {result.failed > 0 && <li>Failed: {result.failed}</li>}
                  </ul>
                  {result.errors && result.errors.length > 0 && (
                    <>
                      <strong>Errors:</strong>
                      <ul style={{ paddingLeft: '1.5em' }}>
                        {result.errors.map((err) => (
                          <li key={err}>{err}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </>
              }
              visible
              position="inline"
              dismissible={false}
            />
          </div>
        )}

        {error && (
          <div style={{ marginTop: '1.5em' }}>
            <StatusBanner
              variant="error"
              message={error}
              visible
              position="inline"
              dismissible={false}
            />
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        {result?.success ? (
          <Button variant="filled" onClick={handleClose}>Close</Button>
        ) : (
          <>
            <Button variant="outlined" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button variant="filled" onClick={handleSend} loading={loading} disabled={loading}>
              Send Invitations
            </Button>
          </>
        )}
      </Modal.Actions>
    </Modal>
  );
}
